import type { AdminOverviewDto, HealthItemDto, OverviewDto } from "@bafe/api-client";
import { Card, EmptyState, InlineNotice, PageHeader, Section, StatusPill } from "@bafe/ui";
import { formatBytes } from "@bafe/validation";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { AccountStorageSummary } from "../components/AccountStorageSummary";
import { LinkButton } from "../components/LinkButton";
import { QueryState } from "../components/QueryState";
import { api } from "../lib/api";
import { formatRelative } from "../lib/format";
import { queryKeys } from "../lib/query";
import { useSession } from "../lib/session";
import { CHECK_STATE } from "../lib/status";

export function Overview() {
	const session = useSession();
	if (session.data?.platformOwner && session.data.memberships.length === 0) {
		return <OperatorOverview />;
	}
	return <MemberOverview />;
}

function MemberOverview() {
	const overview = useQuery({ queryKey: queryKeys.overview, queryFn: () => api.getOverview() });

	return (
		<div className="flex flex-col gap-8">
			<QueryState query={overview} errorTitle="We could not load your overview.">
				{(data) => <OverviewContent data={data} />}
			</QueryState>
		</div>
	);
}

function OverviewContent({ data }: { data: OverviewDto }) {
	const attention = data.health.filter((item) => item.state === "warn" || item.state === "fail");
	const firstAttention = attention[0];

	return (
		<>
			<PageHeader title={data.greeting} description={data.headline} />

			{data.attentionCount > 0 && firstAttention && (
				<InlineNotice
					tone={attention.some((item) => item.state === "fail") ? "critical" : "caution"}
					title={
						data.attentionCount === 1
							? "1 item needs attention"
							: `${data.attentionCount} items need attention`
					}
					action={
						firstAttention.href ? (
							<LinkButton
								to={firstAttention.href}
								size="sm"
								variant="secondary"
								iconRight={<ArrowRightIcon className="size-4" aria-hidden="true" />}
							>
								Review
							</LinkButton>
						) : undefined
					}
				>
					{firstAttention.detail}
				</InlineNotice>
			)}

			<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<Card className="flex flex-col gap-3 sm:col-span-2">
					<CardLabel>Storage used</CardLabel>
					<AccountStorageSummary storage={data.storage} showCounts={false} />
					<Link
						to="/manage/billing"
						className="text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
					>
						Increase storage
					</Link>
				</Card>

				<StatCard
					label="Domains"
					value={String(data.storage.domainCount)}
					footer={<Link className="underline underline-offset-2" to="/manage/domains">Manage domains</Link>}
				/>

				<StatCard
					label="Email accounts"
					value={String(data.storage.mailboxCount)}
					footer={<Link className="underline underline-offset-2" to="/mail/mailboxes">Manage email accounts</Link>}
				/>

				<Card className="flex flex-col gap-2 sm:col-span-2 xl:col-span-4">
					<CardLabel>Storage plan</CardLabel>
					{data.subscription ? (
						<>
							<p className="text-lg font-semibold text-(--text-primary)">
								{data.subscription.plan.name}
							</p>
							<p className="text-sm text-(--text-secondary)">
								{data.subscription.plan.storageLabel} shared across every domain and email account
								on this account.
							</p>
						</>
					) : (
						<>
							<p className="text-lg font-semibold text-(--text-primary)">No plan yet</p>
							<p className="text-sm text-(--text-secondary)">
								Choose a storage plan to start sending and receiving.
							</p>
						</>
					)}
					<Link
						to="/manage/billing"
						className="text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
					>
						{data.subscription ? "View billing" : "Choose a plan"}
					</Link>
				</Card>
			</div>

			<Section title="Email health" description="What Key checked the last time it looked at your domains.">
				{data.health.length === 0 ? (
					<Card>
						<EmptyState
							title="Nothing to check yet"
							description="Add your first domain and Key will start watching its mail setup."
							action={
								<LinkButton to="/manage/domains/new" variant="primary">
									Add a domain
								</LinkButton>
							}
						/>
					</Card>
				) : (
					<Card flush>
						<ul>
							{data.health.map((item) => (
								<li
									key={item.id}
									className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3 last:border-0"
								>
									<div className="flex min-w-0 flex-col gap-0.5">
										<p className="text-sm font-medium text-(--text-primary)">{item.label}</p>
										<p className="text-sm text-(--text-secondary)">{item.detail}</p>
									</div>
									<div className="flex items-center gap-3">
										<StatusPill tone={CHECK_STATE[item.state].tone}>
											{CHECK_STATE[item.state].label}
										</StatusPill>
										<HealthLink item={item} />
									</div>
								</li>
							))}
						</ul>
					</Card>
				)}
			</Section>

			<Section title="Recent activity">
				{data.activity.length === 0 ? (
					<Card>
						<EmptyState
							title="Nothing has happened yet"
							description="Changes to domains, email accounts and billing show up here."
						/>
					</Card>
				) : (
					<Card flush>
						<ul>
							{data.activity.map((entry) => (
								<li
									key={entry.id}
									className="flex flex-wrap items-baseline justify-between gap-2 border-b border-(--border-subtle) px-4 py-3 last:border-0"
								>
									<p className="text-sm text-(--text-primary)">{entry.summary}</p>
									<p className="text-xs text-(--text-muted)">{formatRelative(entry.createdAt)}</p>
								</li>
							))}
						</ul>
					</Card>
				)}
			</Section>
		</>
	);
}

function OperatorOverview() {
	const overview = useQuery({ queryKey: ["admin-overview"], queryFn: () => api.adminOverview() });
	const customers = useQuery({ queryKey: ["admin-customers"], queryFn: () => api.adminSearchCustomers("") });

	return (
		<div className="flex flex-col gap-8">
			<PageHeader
				title="Platform"
				description="You are signed in as the platform owner. Domains, mail, Hub and Site stay with each workspace."
			/>
			<QueryState query={overview} errorTitle="We could not load the platform.">
				{(data) => <OperatorStats data={data} />}
			</QueryState>
			<Section title="Workspaces" description="Customers on this server.">
				<QueryState query={customers} errorTitle="We could not load workspaces.">
					{(page) =>
						page.data.length === 0 ? (
							<EmptyState title="No workspaces yet" description="A workspace appears here when someone creates one." />
						) : (
							<div className="table-wrap">
								<table>
									<thead>
										<tr>
											<th>Workspace</th>
											<th>Plan</th>
											<th>Domains</th>
											<th>Mailboxes</th>
											<th>Storage</th>
										</tr>
									</thead>
									<tbody>
										{page.data.map((customer) => (
											<tr key={customer.organisationId}>
												<td>{customer.name}</td>
												<td className="quiet">{customer.planName ?? "No plan"}</td>
												<td className="quiet">{customer.domainCount}</td>
												<td className="quiet">{customer.mailboxCount}</td>
												<td className="quiet">
													{customer.usedBytes > 0 ? formatBytes(customer.usedBytes) : "0 B"}
													{customer.totalBytes > 0 ? ` of ${formatBytes(customer.totalBytes)}` : ""}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)
					}
				</QueryState>
			</Section>
		</div>
	);
}

function OperatorStats({ data }: { data: AdminOverviewDto }) {
	return (
		<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
			<StatCard
				label="Workspaces"
				value={String(data.activeCustomers)}
				footer={<Link className="underline underline-offset-2" to="/manage/workspaces">View workspaces</Link>}
			/>
			<StatCard label="Domains" value={String(data.activeDomains)} />
			<StatCard label="Mailboxes" value={String(data.activeMailboxes)} />
			<StatCard
				label="Mail server"
				value={data.platformReachable ? "Reachable" : "Not responding"}
				footer={<Link className="underline underline-offset-2" to="/manage/platform/mail/health">Health</Link>}
			/>
			<StatCard
				label="Failed jobs"
				value={String(data.failedProvisioningJobs)}
				footer={<Link className="underline underline-offset-2" to="/manage/platform/mail/queues">Queues</Link>}
			/>
			<StatCard label="Domains needing attention" value={String(data.degradedDomains)} />
			<StatCard label="Migration failures" value={String(data.migrationFailures)} />
			<StatCard
				label="Open abuse"
				value={String(data.openAbuseEvents)}
				footer={<Link className="underline underline-offset-2" to="/manage/platform/mail/abuse">Abuse</Link>}
			/>
		</div>
	);
}

function HealthLink({ item }: { item: HealthItemDto }) {
	if (!item.href) return null;
	return (
		<Link
			to={item.href}
			className="shrink-0 text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
		>
			Review
			<span className="sr-only"> {item.label}</span>
		</Link>
	);
}

function CardLabel({ children }: { children: ReactNode }) {
	return <p className="text-xs font-medium uppercase tracking-wide text-(--text-muted)">{children}</p>;
}

function StatCard({ label, value, footer }: { label: string; value: string; footer?: ReactNode }) {
	return (
		<Card className="flex flex-col gap-2">
			<CardLabel>{label}</CardLabel>
			<p className="text-2xl font-semibold tracking-tight text-(--text-primary)">{value}</p>
			{footer && <p className="text-sm text-(--color-brand-700)">{footer}</p>}
		</Card>
	);
}
